import { Ingredient } from "../models/ingredient";
import type { Measure } from "../models/shopping-list";
import { convertMeasure, unitLabel, type UnitSystem } from "./units";
import type { TFunc } from "../i18n/i18n";

// Display name for a shopping list: the user's name when set, else the localised
// default. The primary list is created unnamed (empty name) so its label follows
// the active locale until the user renames it — see ensurePrimaryList.
export const shoppingListDisplayName = (
  name: string | null | undefined,
  t: TFunc,
): string => (name && name.trim() ? name : t("shopping.defaultListName"));

// Fraction is stored as the FractionUnit enum KEY (see getQuantityString).
const FRACTION_VALUE: Record<string, number> = {
  Eighth: 0.125,
  Quarter: 0.25,
  Half: 0.5,
  ThreeQuarters: 0.75,
  Third: 1 / 3,
  TwoThirds: 2 / 3,
};

// Numeric amount from quantity + fraction; null when neither is present/parseable
// (e.g. "to taste" / no quantity).
function amountOf(ing: Ingredient): number | null {
  const hasFraction = !!ing.fraction && FRACTION_VALUE[ing.fraction] !== undefined;
  const q =
    ing.quantity != null && ing.quantity !== "" ? parseFloat(ing.quantity) : NaN;
  if (Number.isNaN(q) && !hasFraction) return null;
  return (Number.isNaN(q) ? 0 : q) + (hasFraction ? FRACTION_VALUE[ing.fraction!] : 0);
}

// Pretty-print an amount, using fraction glyphs for common parts (2.5 → "2½").
const GLYPHS: [number, string][] = [
  [0.75, "¾"],
  [2 / 3, "⅔"],
  [0.5, "½"],
  [1 / 3, "⅓"],
  [0.25, "¼"],
  [0.125, "⅛"],
];
function formatAmount(n: number): string {
  const whole = Math.floor(n + 1e-9);
  const frac = n - whole;
  const glyph = GLYPHS.find(([v]) => Math.abs(frac - v) < 0.02);
  if (glyph) return (whole ? String(whole) : "") + glyph[1];
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

export interface ConsolidatedItem {
  name: string;
  // Canonical numeric lines (summed per unit) + non-numeric notes, formatted at
  // display time so the unit system can be applied live (formatShoppingQuantity).
  measures: Measure[];
  extras: string[];
  // Distinct source recipes that contributed this line.
  recipeIds: string[];
}

// Words that end in "s"/"es" but are already singular — never strip a suffix.
const NEVER_SINGULARISE = new Set([
  "molasses",
  "hummus",
  "couscous",
  "asparagus",
  "watercress",
  "swiss",
  "bass",
  "series",
  "species",
]);

// Best-effort singular of one word, so "eggs" and "egg" share a key. English is
// irregular; this covers the common grocery cases and errs on the side of leaving
// a word untouched rather than mangling it.
export function singularise(word: string): string {
  const w = word.toLowerCase();
  if (w.length <= 3 || NEVER_SINGULARISE.has(w)) return w;
  if (/(ss|us|is|ous)$/.test(w)) return w; // glass, citrus, iris, couscous
  if (/ies$/.test(w)) return w.slice(0, -3) + "y"; // berries → berry
  if (/(ch|sh|x|z|o)es$/.test(w)) return w.slice(0, -2); // tomatoes → tomato, boxes → box
  if (/s$/.test(w)) return w.slice(0, -1); // eggs → egg, onions → onion
  return w;
}

// Case-insensitive matching key for a shopping-list name. Collapses whitespace and
// singularises the final word ("large eggs" and "Large Egg" both → "large egg"), so
// singular/plural variants consolidate onto one line.
export function shoppingItemKey(name: string): string {
  const words = name.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  words[words.length - 1] = singularise(words[words.length - 1]);
  return words.join(" ");
}

// Consolidate a flat list of recipe ingredients into shopping-list lines:
// - group by name (case-insensitive), skipping section headings;
// - within a name, sum amounts that share a unit; different units are listed
//   together ("200 g + 1 cup"); non-numeric notes (e.g. "to taste") are appended.
// Returns canonical `measures` (raw amount + unit) + non-numeric `extras` per
// item; the unit system is applied later at display time (formatShoppingQuantity)
// so items reconvert live when the preference changes.
export function consolidateIngredients(ingredients: Ingredient[]): ConsolidatedItem[] {
  const byName = new Map<
    string,
    {
      name: string;
      units: Map<string, { sum: number; numeric: boolean }>;
      extras: Set<string>;
      recipes: Set<string>;
    }
  >();

  for (const ing of ingredients) {
    if (ing.group) continue;
    const name = ing.name?.trim();
    if (!name) continue;
    const key = shoppingItemKey(name);

    let entry = byName.get(key);
    if (!entry) {
      entry = { name, units: new Map(), extras: new Set(), recipes: new Set() };
      byName.set(key, entry);
    }
    if (ing.recipeId) entry.recipes.add(ing.recipeId);

    const amt = amountOf(ing);
    if (amt !== null) {
      const unit = (ing.unit ?? "").trim();
      const u = entry.units.get(unit) ?? { sum: 0, numeric: false };
      u.sum += amt;
      u.numeric = true;
      entry.units.set(unit, u);
    } else if (ing.other?.trim()) {
      entry.extras.add(ing.other.trim());
    }
  }

  const result: ConsolidatedItem[] = [];
  for (const entry of byName.values()) {
    const measures: Measure[] = [];
    for (const [unit, u] of entry.units) {
      if (u.numeric) measures.push({ amount: u.sum, unit });
    }
    result.push({
      name: entry.name,
      measures,
      extras: [...entry.extras],
      recipeIds: [...entry.recipes],
    });
  }

  result.sort((a, b) => a.name.localeCompare(b.name));
  return result;
}

// Format an item's canonical measures + extras into display text, converting each
// numeric line to `unitSystem`. Joins lines with " + " (e.g. "200 g + 1 cup").
// Falls back to the legacy/manual `quantity` string when there are no measures.
export function formatShoppingQuantity(
  item: { measures?: Measure[] | null; extras?: string[] | null; quantity?: string | null },
  unitSystem: UnitSystem = "original",
  t?: TFunc,
): string {
  const parts: string[] = [];
  for (const m of item.measures ?? []) {
    const { value, unit } = convertMeasure(m.amount, m.unit, unitSystem);
    parts.push(
      [formatAmount(value), unitLabel(unit, value, t)].filter(Boolean).join(" ").trim(),
    );
  }
  for (const e of item.extras ?? []) parts.push(e);
  if (parts.length) return parts.join(" + ");
  return item.quantity ?? "";
}

export interface ShoppingImportClassification {
  /** Not on the list at all. */
  newItems: ConsolidatedItem[];
  /** Already on the list, not ticked off. */
  presentActive: ConsolidatedItem[];
  /** Already on the list and ticked off. */
  presentChecked: ConsolidatedItem[];
}

// Split consolidated import items by how they relate to what's already on the
// list (matched by name key), so the UI can tell the user what's new vs. already
// there — and whether some are ticked off — before adding anything.
export function classifyShoppingImport(
  items: ConsolidatedItem[],
  liveItems: { name: string; checked: boolean; deletedAt?: string | null }[],
): ShoppingImportClassification {
  const checkedByKey = new Map<string, boolean>();
  for (const i of liveItems) {
    if (i.deletedAt) continue;
    checkedByKey.set(shoppingItemKey(i.name), i.checked);
  }
  const out: ShoppingImportClassification = {
    newItems: [],
    presentActive: [],
    presentChecked: [],
  };
  for (const it of items) {
    const key = shoppingItemKey(it.name);
    if (!checkedByKey.has(key)) out.newItems.push(it);
    else if (checkedByKey.get(key)) out.presentChecked.push(it);
    else out.presentActive.push(it);
  }
  return out;
}
